import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { inject, observer } from 'mobx-react'
import { reaction, observable } from 'mobx'
import get from 'lodash/get'
import isNaN from 'lodash/isNaN'
import moment from 'moment-timezone'
import {
  addDays,
  setSeconds,
  setMilliseconds,
} from 'date-fns'

import TimerModel, { NULL as TimerModelNULL } from './model'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import { NULL as BlockModelNULL } from '@client/components/Block/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import { SECTION_TYPES } from '@client/components/Section/constants'
import { APP_CONFIG_TIMER_URL } from '@config/app.config'
import findAncestor from '@client/utils/findAncestor'
import { getPaddingTotal } from '../../Block/component'

const getPaddingReduction = ({ kind, section, columnIndex, fullWidth }) => {
  if (fullWidth) {
    return 0
  }
  const sectionType = get(section, 'sectionType')
  if (
    sectionType === SECTION_TYPES.LANDINGONECOL ||
    sectionType === SECTION_TYPES.LANDINGTWOCOL
  ) {
    return 0
  }
  return getPaddingTotal({ kind, section, columnIndex, fullWidth })
}

export class Timer extends Component {
  @observable editingSettings = BlockModelNULL

  static propTypes = {
    model: PropTypes.instanceOf(TimerModel),
    editor: PropTypes.instanceOf(EditorModel),
    app: PropTypes.instanceOf(AppModel),
    serverRender: PropTypes.bool,
  }

  static defaultProps = {
    model: TimerModelNULL,
    editor: EditorModelNULL,
    app: AppModelNULL,
    serverRender: false,
  }

  disposeReaction = null

  constructor(props) {
    super(props)

    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
    this.disposeReaction = reaction(
      () => this.editingSettings,
      (values) => {
        this.setContainerDimentions()
      }
    )

    // Toggling fullWidth changes the padding reduction in setContainerDimentions
    // but does NOT physically resize the observed tableCell, so ResizeObserver
    // won't fire. Use a mobx reaction (not componentDidUpdate prevProps) — under
    // @observer, prevProps.model and this.props.model reference the same
    // instance, so prevProps.model.settings.values.fullWidth resolves to the
    // new value too and the comparison would always be equal.
    const model = props.model
    this.disposeFullWidthReaction = reaction(
      () => model.settings.values.fullWidth,
      () => {
        this.setContainerDimentions()
      }
    )

    this.el = React.createRef()
  }

  componentDidMount() {
    this.tableCell = this.getTableCell()
    this.setContainerDimentions()
    if (this.tableCell) {
      this.resizeObserver = new ResizeObserver(() => this.setContainerDimentions())
      this.resizeObserver.observe(this.tableCell)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.editingSettings = this.props.app?.editingSettings || BlockModelNULL
  }

  componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    if (this.disposeReaction) {
      this.disposeReaction()
    }
    if (this.disposeFullWidthReaction) {
      this.disposeFullWidthReaction()
    }
  }

  getTableCell = () => {
    return findAncestor(this.el.current, 'gan-block-parent')
  }

  setContainerDimentions() {
    const section = get(this.props, 'model.section')
    const columnIndex = this.props.model.columnIndex
    const kind = this.props.model.kind
    const fullWidth = this.props.model.settings.values.fullWidth

    const padding = getPaddingReduction({ kind, section, columnIndex, fullWidth })

    const tableCell = this.tableCell
    const clientWidth = get(tableCell, 'clientWidth')
    this.props.model.setContainerDimensions({
      width: clientWidth - padding,
    })
  }

  getDefaultDateValue = () => {
    const dateValue = addDays(setMilliseconds(setSeconds(
      new Date(moment().format()
    ), 0), 0), 3).toISOString()
    return dateValue
  }

  render() {
    const { model, serverRender, app, editor } = this.props
    const globalSettings = this.props.editor.settings.values
    const settings = model.settings.values
    const width = settings.width
    // The path is deterministic (same settings -> same URL), so email
    // clients/proxies can serve a GIF cached from an earlier send. Bust the
    // cache per server render (one render per send/test-send), but not in the
    // editor where a changing URL would re-fetch the GIF on every re-render.
    const cacheBust = serverRender ? `?v=${Date.now()}` : ''
    const imageUrl = `${APP_CONFIG_TIMER_URL}/${
      encodeURIComponent(settings.dateValue || this.getDefaultDateValue())
    }/${
      encodeURIComponent(settings.textColor.replace('#', ''))
    }/${
      encodeURIComponent(settings.backgroundColor.replace('#', ''))
    }/${
      encodeURIComponent(settings.language || editor.language || app.locale)
    }.gif${cacheBust}`
    const altText = settings.dateValue ? settings.dateValue.trim() : ''

    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const fullWidthClass = this.props.model.settings.values.fullWidth
      ? 'full-width'
      : ''
    const className = `${serverRender ? '' : 'timer'} ${fullWidthClass}`

    const widthCheckCondition = !!width && typeof width !== 'undefined' && !isNaN(width) && width !== 'NaN'

    const imgStyle = {
      minHeight: serverRender ? undefined : '20px',
      display: 'block',
      border: 0,
      height: 'auto',
      margin: 0,
      width: widthCheckCondition ? `${width}px` : undefined,
      maxWidth: widthCheckCondition ? `100%` : undefined,
      fontSize: fontSize + 'px',
    }

    return (
      <img
        className={className}
        role="img"
        ref={this.el}
        src={imageUrl}
        alt={altText}
        width={widthCheckCondition ? width : undefined}
        height={widthCheckCondition ? Math.round((width * 175) / 1200) : undefined}
        style={imgStyle}
      />
    )
  }
}

export default inject('app', 'editor', 'serverRender')(observer(Timer))
